import { NO_DATA_SIGN, NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import CountryService from "app/services/CountryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import dateTimeService from "app/services/date-time/dateTimeService";

import { NUMBER_OF_HIGH_PERCENTAGE_VALUE, NUMBER_OF_VISIBLE_ITEMS } from "../constants";
import type {
    ListFormatValueResponse,
    PercentageRoundedValueResponse,
    PrimitiveListCellValue,
    TrendItem,
} from "../types";

const percentageFormatterFilter = percentageFilter();

export const isUpgradeCellValuePresent = <V>(value: V): boolean => value === "grid.upgrade";

const defaultFormatPrimitiveListValue = (value: readonly PrimitiveListCellValue[]): string =>
    `${value[0]} +${value.length - 1}`;

export const createPrimitiveListCellValue = ({
    value,
    formatValue = defaultFormatPrimitiveListValue,
    fallback = NO_DATA_SIGN,
}: {
    fallback?: string;
    formatValue?: (value: readonly PrimitiveListCellValue[]) => string | number;
    value: readonly PrimitiveListCellValue[];
}): string | number => {
    if (!value || value.length === 0) {
        return fallback;
    }

    if (value.length === 1) {
        return value[0];
    }

    return formatValue(value);
};

export const createLocationCodesCellValue = (value: PrimitiveListCellValue | readonly number[]): readonly number[] => {
    switch (true) {
        case is.string(value):
            if (value.trim()) {
                return value
                    .split(",")
                    .map((country) => Number(country))
                    .filter((num) => !isNaN(num));
            }

            return [];
        case is.readonlyArray<number>(value):
            return value;
        case is.number(value) && CountryService.isUnknownCountry(value):
            return [WORLDWIDE_COUNTRY_ID];
        default:
            return [value];
    }
};

export const getListFormatValue = (value: string[]): ListFormatValueResponse => {
    const isOverflow = value.length > NUMBER_OF_VISIBLE_ITEMS;
    const visibleItems = isOverflow ? value.slice(0, NUMBER_OF_VISIBLE_ITEMS) : value;
    const formattedItems = `${visibleItems.join(", ")}${isOverflow ? ` +${value.length - NUMBER_OF_VISIBLE_ITEMS}` : ""}`;

    return { isOverflow, visibleItems, formattedItems };
};

export const getPercentageRoundedValue = (value?: readonly [number, number]): PercentageRoundedValueResponse => {
    const hasValues = (value?.[0] ?? 0) > 0 || (value?.[1] ?? 0) > 0;
    const fillPercent = value && value[0] ? value[0] * 100 : 0;

    const leftValue = hasValues ? percentageFormatterFilter(value?.[0], 1) + "%" : NOT_AVAILABLE;
    const rightValue = hasValues ? percentageFormatterFilter(value?.[1], 1) + "%" : NOT_AVAILABLE;

    return { fillPercent, leftValue, rightValue, hasValues };
};

export const trendItemsToSparklineData = (value: readonly TrendItem[]): readonly number[] => {
    return value
        .reduce<{ key: number; value: number }[]>((acc, item) => {
            const dateKey = item.Key || item.key;

            if (dateKey) {
                const key = dateTimeService.getUTCMoment(dateKey as string).valueOf();

                if (!isNaN(key)) {
                    acc.push({ key, value: (item.Value ?? item.value) as number });
                }
            }

            return acc;
        }, [])
        .sort((a, b) => a.key - b.key)
        .map((item) => item.value);
};

export const getItemPlusNValue = (value: string | readonly string[]) => {
    if (!Array.isArray(value)) {
        return value;
    }

    const items = value as readonly string[];
    const [first, ...rest] = items;
    let cellText = `${first}`;

    if (rest.length > 0) {
        cellText += ` +${rest.length}`;
    }

    return cellText;
};

export const truncateToDecimalPlaces = (value: number, decimals: number): string => {
    if (!Number.isFinite(value)) {
        return String(value);
    }

    const str = value.toFixed(decimals + 8);
    return str.slice(0, str.indexOf(".") + decimals + 1);
};

export const getChangeFormattedValue = (value: number | string | null, formatFn: () => string): string => {
    if (value === "Infinity" || value === NUMBER_OF_HIGH_PERCENTAGE_VALUE) {
        return translate("grow.lead_generator.exist.table.cell.infinite_change");
    }
    if (value === "N/A" || value === null) {
        return "N/A";
    }
    return formatFn();
};
